// Shared logic hook for StyleOutfit page — used by both Mobile and Desktop variants.
// Owns photo state, generation flows, preview-sheet state, and credit gating.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edge-error";
import { errorMessage } from "@/lib/error-message";
import { compressImage, uploadToBucket, signedUrl } from "@/lib/storage";
import { requestTryonRefund } from "@/lib/refund-tryon";
import { OUTFIT_LOOKS } from "@/lib/outfit-looks";
import { SINGLE_ITEMS } from "@/lib/single-items";
import { VIBES } from "@/lib/vibes";
import {
  ITEM_TYPE_LABELS, OUTFIT_CATEGORY_LABELS,
  type Gender, type OutfitLookV2, type SingleItemV2, type VibeEntry,
} from "@/lib/style-types";
import type { CustomizeData } from "@/components/CustomizeOutfitDialog";
import { usePaywallTriggers, type PaywallVariant } from "@/hooks/usePaywallTriggers";
import { useFreeTierGate } from "@/contexts/FreeTierGateContext";
import { setPendingGeneration, clearPendingGeneration, waitForTryonComplete } from "@/hooks/usePendingGeneration";

export const STAGES = [
  "Analyzing your photo…",
  "Picking the look…",
  "Generating image…",
  "Scoring the style…",
];

export type Tab = "vibes" | "outfits" | "items";
export type VibeMode = "scene" | "outfit";

const filterByGender = <T extends { genders: Gender[] }>(arr: T[], g: Gender) =>
  g === "all" ? arr : arr.filter((x) => x.genders.includes(g));

export function useStyleOutfit() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { credits, refresh: refreshCredits } = useCredits();
  const { checkBeforeTryon } = usePaywallTriggers();
  const { runGate } = useFreeTierGate();

  const [tab, setTab] = useState<Tab>("vibes");
  const [genderByTab, setGenderByTab] = useState<Record<Tab, Gender>>({
    vibes: "all", outfits: "all", items: "all",
  });
  const gender = genderByTab[tab];
  const setGender = (g: Gender) => setGenderByTab((s) => ({ ...s, [tab]: g }));

  const [outfitCategory, setOutfitCategory] = useState<OutfitLookV2["category"] | "all">("all");
  const [itemType, setItemType] = useState<SingleItemV2["type"] | "all">("all");

  const [userPhotoFile, setUserPhotoFile] = useState<File | null>(null);
  const [userPhotoPreview, setUserPhotoPreview] = useState<string | null>(null);
  const [savedPhotoPath, setSavedPhotoPath] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stageIdx, setStageIdx] = useState(0);
  const [genError, setGenError] = useState<string | null>(null);
  const lastAttemptRef = useRef<(() => void) | null>(null);

  const [customOpen, setCustomOpen] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyDefaultPack, setBuyDefaultPack] = useState<string | undefined>(undefined);
  const [outOfCreditsOpen, setOutOfCreditsOpen] = useState(false);
  const [highDemandOpen, setHighDemandOpen] = useState(false);
  const [paywall, setPaywall] = useState<PaywallVariant | null>(null);
  const paywallResolverRef = useRef<((bought: boolean) => void) | null>(null);

  /** Show paywall, await user decision. Returns true if they tapped Buy. */
  const showPaywallAndWait = (variant: PaywallVariant): Promise<boolean> => {
    setPaywall(variant);
    return new Promise<boolean>((resolve) => { paywallResolverRef.current = resolve; });
  };
  /** Wired to PaywallModal.onDismiss — close + resolve(false). */
  const handlePaywallDismiss = () => {
    setPaywall(null);
    const r = paywallResolverRef.current; paywallResolverRef.current = null;
    r?.(false);
  };
  /** Wired to PaywallModal.onBuy — close + open BuyCredits + resolve(true). */
  const handlePaywallBuy = (packId: "mini" | "value") => {
    setBuyDefaultPack(packId);
    setBuyOpen(true);
    setPaywall(null);
    const r = paywallResolverRef.current; paywallResolverRef.current = null;
    r?.(true);
  };

  const [previewVibe, setPreviewVibe] = useState<VibeEntry | null>(null);
  const [previewLook, setPreviewLook] = useState<OutfitLookV2 | null>(null);
  const [previewItem, setPreviewItem] = useState<SingleItemV2 | null>(null);

  const [customItemFile, setCustomItemFile] = useState<File | null>(null);
  const [customItemPreview, setCustomItemPreview] = useState<string | null>(null);
  const [customItemSheetOpen, setCustomItemSheetOpen] = useState(false);
  const [customItemLabel, setCustomItemLabel] = useState("");

  const [vibeMode, setVibeMode] = useState<VibeMode>("scene");

  // Pull most recent photo. Gender filter defaults to "all" so users see every style.
  useEffect(() => {
    if (!user) return;
    supabase
      .from("tryons").select("user_photo_url").eq("user_id", user.id)
      .order("created_at", { ascending: false }).limit(1).maybeSingle()
      .then(async ({ data }) => {
        if (data?.user_photo_url) {
          setSavedPhotoPath(data.user_photo_url);
          try {
            const u = await signedUrl("user-photos", data.user_photo_url);
            setUserPhotoPreview(u);
          } catch { /* ignore */ }
        }
      });
  }, [user]);

  useEffect(() => {
    if (!busy) { setStageIdx(0); return; }
    const id = setInterval(() => setStageIdx((i) => Math.min(i + 1, STAGES.length - 1)), 12000);
    return () => clearInterval(id);
  }, [busy]);

  useEffect(() => {
    if (tab === "outfits") setOutfitCategory("all");
    if (tab === "items") setItemType("all");
  }, [tab]);

  const onPickPhoto = async (file?: File) => {
    if (!file) return;
    try {
      // Higher quality for the user photo so the "Before" stays as crisp
      // as the AI-generated "After" in the Before/After slider.
      const compressed = await compressImage(file, 2.5, 2048);
      setUserPhotoFile(compressed);
      setUserPhotoPreview(URL.createObjectURL(compressed));
      setSavedPhotoPath(null);
    } catch (e) {
      toast({ title: "Couldn't read image", description: String(e), variant: "destructive" });
    }
  };

  const ensureUploadedPhoto = async (): Promise<string> => {
    if (savedPhotoPath) return savedPhotoPath;
    if (!userPhotoFile || !user) throw new Error("No photo");
    const path = await uploadToBucket("user-photos", user.id, userPhotoFile, "jpg");
    setSavedPhotoPath(path);
    return path;
  };

  const preflight = async (): Promise<boolean> => {
    if (!user) return false;
    if (!userPhotoPreview) {
      toast({ title: "Add your photo first", variant: "destructive" });
      return false;
    }
    if ((credits ?? 0) < 1) { setOutOfCreditsOpen(true); return false; }
    const trig = checkBeforeTryon();
    if (trig) {
      // Show emotional paywall. If user buys → BuyCreditsDialog takes over (stop here).
      // If user taps "Maybe later" → fall through to ad-gate so they can still
      // earn this credit by watching an ad.
      const bought = await showPaywallAndWait(trig.variant);
      if (bought) return false;
    }
    // Free-tier ad-gate (Android only). 1st free, 2nd & 3rd require ad, 4th = paywall.
    const allowed = await runGate();
    if (!allowed) return false;
    return true;
  };

  const generateOutfit = async (
    data: CustomizeData,
    label: string,
    kind: "vibe" | "look" | "custom",
    extras?: { mode?: "scene" | "outfit" },
  ) => {
    if (!user || !(await preflight())) return;
    lastAttemptRef.current = () => { void generateOutfit(data, label, kind, extras); };
    setBusy(true);
    setGenError(null);
    let pendingId: string | null = null;
    try {
      const userPath = await ensureUploadedPhoto();
      const userUrl = await signedUrl("user-photos", userPath);

      const { data: pending, error: pendErr } = await supabase
        .from("tryons").insert({
          user_id: user.id,
          user_photo_url: userPath,
          status: "processing",
          kind: "outfit_only",
          product_category: kind,
          product_style: label,
        }).select("id").single();
      if (pendErr) throw pendErr;
      pendingId = pending.id;
      setPendingGeneration({ id: pendingId, kind: "outfit", startedAt: Date.now() });

      const referenceImageUrls: string[] = [];
      for (const f of data.referenceFiles ?? []) {
        const p = await uploadToBucket("product-images", user.id, f, "jpg");
        referenceImageUrls.push(await signedUrl("product-images", p));
      }
      const validLinks = (data.referenceLinks ?? [])
        .map((l) => l?.trim())
        .filter((l): l is string => !!l && /^https?:\/\//i.test(l));
      for (const link of validLinks) {
        try {
          const { data: ext } = await supabase.functions.invoke("extract-product", { body: { url: link } });
          if (ext?.imageUrl) referenceImageUrls.push(ext.imageUrl);
        } catch { /* skip */ }
      }

      const { data: profile } = await supabase
        .from("profiles").select("*").eq("user_id", user.id).maybeSingle();

      // Fire the edge function. It will upload + finalize the row server-side,
      // so even if this request is aborted (refresh/close) the row will still
      // reach `complete` and the resume hook will surface it next session.
      const invokePromise = supabase.functions.invoke("generate-outfit", {
        body: {
          userPhotoUrl: userUrl,
          baseProductImageUrl: null,
          baseProduct: null,
          profile,
          customPrompt: data.prompt || undefined,
          referenceImageUrls: referenceImageUrls.length ? referenceImageUrls : undefined,
          mode: extras?.mode,
          tryonId: pendingId,
        },
      });

      // Drive navigation off the row state, not the HTTP response.
      const [terminal, invoke] = await Promise.all([
        waitForTryonComplete(pendingId),
        invokePromise.catch((e) => ({ data: null, error: e })),
      ]);

      // If the invoke hit a credit/auth/rate error, surface it.
      if (invoke.error) {
        const info = await readEdgeError(invoke.error);
        if (info.status === 402) { await refreshCredits(); setOutOfCreditsOpen(true); clearPendingGeneration(); return; }
        const isPremium = Number(profile?.credits_purchased_total ?? 0) > 0;
        if (!isPremium && info.status === 429) {
          setHighDemandOpen(true);
          clearPendingGeneration();
          return;
        }
        if (info.status === 401) {
          toast({ title: "Session expired", description: "Please sign in again to continue.", variant: "destructive" });
          clearPendingGeneration();
          return;
        }
        if (terminal?.status !== "complete") throw new Error(info.message);
      }

      if (terminal?.status === "failed") throw new Error("Generation failed");
      if (!terminal) throw new Error("Generation timed out");

      clearPendingGeneration();
      await refreshCredits();
      navigate(`/app/result/${pendingId}`);
    } catch (e) {
      if (pendingId) {
        await supabase.from("tryons").update({ status: "failed" })
          .eq("id", pendingId).eq("user_id", user.id);
      }
      clearPendingGeneration();
      const { message } = await readEdgeError(e);
      setGenError(message);
      toast({ title: "Generation failed", description: message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const generateSingleItem = async (
    source:
      | { kind: "catalog"; item: SingleItemV2 }
      | { kind: "upload"; file: File; label: string; type?: SingleItemV2["type"] },
  ) => {
    if (!user || !(await preflight())) return;
    lastAttemptRef.current = () => { void generateSingleItem(source); };
    setBusy(true);
    setGenError(null);
    let pendingId: string | null = null;
    try {
      const userPath = await ensureUploadedPhoto();
      const userUrl = await signedUrl("user-photos", userPath);

      const itemBlob = source.kind === "catalog"
        ? await fetch(source.item.image).then((r) => r.blob())
        : source.file;
      const itemPath = await uploadToBucket("product-images", user.id, itemBlob, "jpg");
      const itemUrl = await signedUrl("product-images", itemPath);

      const fallbackCategory = source.kind === "catalog" ? source.item.type : source.type ?? null;
      const fallbackLabel = source.kind === "catalog" ? source.item.label : source.label;

      const { data: pending, error: pendErr } = await supabase
        .from("tryons").insert({
          user_id: user.id,
          user_photo_url: userPath,
          product_image_url: itemPath,
          status: "processing",
          kind: "tryon",
          product_category: fallbackCategory,
          product_style: fallbackLabel,
        }).select("id").single();
      if (pendErr) throw pendErr;
      pendingId = pending.id;
      setPendingGeneration({ id: pendingId, kind: "tryon", startedAt: Date.now() });

      const { data: profile } = await supabase
        .from("profiles").select("*").eq("user_id", user.id).maybeSingle();

      // Edge function uploads + finalizes the row server-side. Even if this
      // tab dies, the row will reach `complete` and the resume hook will
      // surface the result on next load.
      const invokePromise = supabase.functions.invoke("generate-tryon", {
        body: { userPhotoUrl: userUrl, productImageUrl: itemUrl, profile, tryonId: pendingId },
      });

      const [terminal, invoke] = await Promise.all([
        waitForTryonComplete(pendingId),
        invokePromise.catch((e) => ({ data: null, error: e })),
      ]);

      if (invoke.error) {
        const ctx = (invoke.error as { context?: { status?: number } }).context;
        if (ctx?.status === 402) { await refreshCredits(); setOutOfCreditsOpen(true); clearPendingGeneration(); return; }
        const isPremium = Number(profile?.credits_purchased_total ?? 0) > 0;
        if (!isPremium && ctx?.status === 429) {
          setHighDemandOpen(true);
          clearPendingGeneration();
          return;
        }
        if (terminal?.status !== "complete") throw invoke.error;
      }

      if (terminal?.status === "failed") throw new Error("Generation failed");
      if (!terminal) throw new Error("Generation timed out");

      clearPendingGeneration();
      await refreshCredits();
      navigate(`/app/result/${pendingId}`);
    } catch (e) {
      if (pendingId) {
        await supabase.from("tryons").update({ status: "failed" })
          .eq("id", pendingId).eq("user_id", user.id);
        const refund = await requestTryonRefund(pendingId);
        if (refund.refunded) await refreshCredits();
      }
      clearPendingGeneration();
      const msg = errorMessage(e);
      setGenError(msg);
      toast({ title: "Try-on failed", description: msg, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const openVibe = (v: VibeEntry) => { setVibeMode(v.defaultMode); setPreviewVibe(v); };
  const confirmVibe = () => {
    if (!previewVibe) return;
    const v = previewVibe;
    setPreviewVibe(null);
    const prompt = vibeMode === "scene" ? v.scenePrompt : v.outfitPrompt;
    void generateOutfit({ prompt, referenceLinks: [], referenceFiles: [] }, v.label, "vibe", { mode: vibeMode });
  };
  const confirmLook = () => {
    if (!previewLook) return;
    const l = previewLook;
    setPreviewLook(null);
    void generateOutfit({ prompt: l.prompt, referenceLinks: [], referenceFiles: [] }, l.label, "look");
  };
  const confirmItem = () => {
    if (!previewItem) return;
    const i = previewItem;
    setPreviewItem(null);
    void generateSingleItem({ kind: "catalog", item: i });
  };

  const onPickCustomItem = async (file?: File) => {
    if (!file) return;
    try {
      const compressed = await compressImage(file, 0.92, 1024);
      setCustomItemFile(compressed);
      setCustomItemPreview(URL.createObjectURL(compressed));
      setCustomItemLabel(file.name.replace(/\.[^.]+$/, "").slice(0, 40) || "Custom item");
      setCustomItemSheetOpen(true);
    } catch (e) {
      toast({ title: "Couldn't read image", description: String(e), variant: "destructive" });
    }
  };
  const confirmCustomItem = () => {
    if (!customItemFile) return;
    const f = customItemFile;
    const label = customItemLabel || "Custom item";
    setCustomItemSheetOpen(false);
    setCustomItemFile(null);
    setCustomItemPreview(null);
    void generateSingleItem({ kind: "upload", file: f, label });
  };

  const visibleVibes = useMemo(() => filterByGender(VIBES, gender), [gender]);
  const visibleOutfits = useMemo(() => {
    const byGender = filterByGender(OUTFIT_LOOKS, gender);
    return outfitCategory === "all" ? byGender : byGender.filter((l) => l.category === outfitCategory);
  }, [gender, outfitCategory]);
  const visibleItems = useMemo(() => {
    const byGender = filterByGender(SINGLE_ITEMS, gender);
    return itemType === "all" ? byGender : byGender.filter((i) => i.type === itemType);
  }, [gender, itemType]);

  const outfitCategoryChips = useMemo(() => {
    const byGender = filterByGender(OUTFIT_LOOKS, gender);
    const counts = new Map<OutfitLookV2["category"], number>();
    for (const l of byGender) counts.set(l.category, (counts.get(l.category) ?? 0) + 1);
    return Array.from(counts.entries()).map(([id, n]) => ({ id, n, ...OUTFIT_CATEGORY_LABELS[id] }));
  }, [gender]);
  const itemTypeChips = useMemo(() => {
    const byGender = filterByGender(SINGLE_ITEMS, gender);
    const counts = new Map<SingleItemV2["type"], number>();
    for (const i of byGender) counts.set(i.type, (counts.get(i.type) ?? 0) + 1);
    return Array.from(counts.entries()).map(([id, n]) => ({ id, n, ...ITEM_TYPE_LABELS[id] }));
  }, [gender]);

  return {
    // state
    tab, setTab,
    gender, setGender,
    outfitCategory, setOutfitCategory,
    itemType, setItemType,
    userPhotoPreview,
    busy, stageIdx, genError,
    credits,
    customOpen, setCustomOpen,
    buyOpen, setBuyOpen, buyDefaultPack, setBuyDefaultPack,
    outOfCreditsOpen, setOutOfCreditsOpen,
    highDemandOpen, setHighDemandOpen,
    paywall, setPaywall, handlePaywallDismiss, handlePaywallBuy,
    previewVibe, setPreviewVibe,
    previewLook, setPreviewLook,
    previewItem, setPreviewItem,
    customItemPreview, customItemSheetOpen, setCustomItemSheetOpen, setCustomItemFile, setCustomItemPreview,
    customItemLabel, setCustomItemLabel,
    vibeMode, setVibeMode,
    // derived
    visibleVibes, visibleOutfits, visibleItems,
    outfitCategoryChips, itemTypeChips,
    // actions
    onPickPhoto, onPickCustomItem,
    openVibe, confirmVibe, confirmLook, confirmItem, confirmCustomItem,
    generateOutfit, setGenError, lastAttemptRef,
  };
}

export type StyleOutfitCtx = ReturnType<typeof useStyleOutfit>;
